import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/shared/services';
import { ImportTemplateType } from 'src/shared/enums';
import { ZoningCodeService } from 'src/app/core/services';
import { IZoningCodeModel } from 'src/app/interfaces/zoning-code-model.interface';
import { ImportTableWorkflowBaseComponent } from '../shared/import-table-workflow.base';
import IZoneDescriptionImportRowData = ZoneIQCalibration.IZoneDescriptionImportRowData;
import { IZCValidationTableResult } from 'src/app/interfaces/zc-validation-result.interface';
import _ from 'lodash';
import IZoneDescriptionImportValidationResult = ZoneIQCalibration.IZoneDescriptionImportValidationResult;

interface IZoneDescriptionsValidationZoningCodeModel extends IZoningCodeModel {
    messages?: IZCValidationTableResult[];
}

@Component({
    standalone: false,
    selector: 'app-zone-descriptions-import-table',
    templateUrl: './zone-descriptions-import-table.component.html',
    styleUrls: ['./zone-descriptions-import-table.component.scss']
})
export class ZoneDescriptionsImportTableComponent
    extends ImportTableWorkflowBaseComponent<IZoneDescriptionsValidationZoningCodeModel>
    implements OnInit {
    zoningCode: IZoningCodeModel = null;
    readonly ImportTemplateType = ImportTemplateType;

    readonly validationData$ = new BehaviorSubject<IZoningCodeModel | null>(null);
    private validationData: IZoningCodeModel | null = null;

    constructor(
        private readonly zoningCodeService: ZoningCodeService,
        loaderService: LoaderService,
        dialog: MatDialog,
        toastrService: ToastrService
    ) {
        super(loaderService, dialog, toastrService);
    }

    ngOnInit(): void {
        this.loaderService.show();
        this.zoningCodeService.zoningCodes.pipe(filter(zc => zc !== null && zc.length > 0)).subscribe(zc => {
            this.zoningCode = zc[0];
            this.loaderService.hide();
        });
    }

    clearTable(): void {
        this.selectedFileData = null;
        this.resetValidationData();
    }

    protected getValidationData(): IZoningCodeModel {
        return this.validationData;
    }

    protected resetValidationData(): void {
        this.validationData = null;
        this.validationData$.next(null);
    }

    protected setValidationData(result: IZoningCodeModel): void {
        this.validationData = result;
        this.validationData$.next(result);
    }

    protected getImportConfirmationMessage(): string {
        return 'Zone and Overlay descriptions will be imported to the selected Zoning Code. Continue?';
    }

    protected runValidationRequest(): Observable<IZoneDescriptionsValidationZoningCodeModel> {
        return this.zoningCodeService.validateZoneDescriptions(this.zoningCode.Id, this.selectedFileData);
    }

    protected runImportRequest(): Observable<IZoneDescriptionsValidationZoningCodeModel> {
        return this.zoningCodeService.importZoneDescriptions(this.zoningCode.Id, this.selectedFileData);
    }

    get validationRows(): IZoneDescriptionImportRowData[] {
        return this.validationData
            ? _.sortBy(
                  this.validationData.ZoneCombinations.map(zcmb => {
                      return this.mapEntityTableRow(zcmb, 'Zone');
                  })
                      .concat(
                          this.validationData.Overlays.map(ovl => {
                              return this.mapEntityTableRow(ovl, 'Overlay');
                          })
                      )
                      .concat(
                          _.get(
                              this.validationData,
                              'importValidationResult.descriptionsImport.unknownEntities',
                              []
                          ).map(unknownEntity => {
                              return this.mapEntityTableRow(unknownEntity);
                          })
                      ),
                  'importValidationResult.index'
              )
            : [];
    }

    private mapEntityTableRow(entity: any, entityType?: string): IZoneDescriptionImportRowData {
        return {
            entityType: entityType ? entityType : entity.Type,
            entityName: entity.Name,
            description: entity.Description,
            importValidationResult: entity.importValidationResult as IZoneDescriptionImportValidationResult
        };
    }
}
